import { fetchAuthSession } from "aws-amplify/auth";

const API_URL = process.env.REACT_APP_API_URL;

interface FetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: BodyInit | null;
  headers?: HeadersInit;
}

export async function fetchAPI(endpoint: string, options: FetchOptions = {}) {
    const session = await fetchAuthSession();

    if (!session || !session.tokens) {
        return fetchAPIInternal(endpoint, options);
    }
    const token = session.tokens.accessToken.toString();
    return fetchAPIInternal(endpoint, options, token);
}

export function fetchAPIInternal(endpoint: string, options: FetchOptions = {}, token?: string) {
  const url = `${API_URL}${endpoint}`;

  // Initialize headers if not already present
  if (!options.headers) {
    options.headers = {};
  }

  // Set default headers and merge with any existing ones
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add the Authorization header if a token is provided
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Merge headers, overwriting defaults with any specified in options.headers
  options.headers = { ...defaultHeaders, ...options.headers };

  // Use fetch to make the HTTP request
  return fetch(url, options)
    .then(response => {
      // Check if the HTTP response is not OK to throw an error
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response.json(); // Assuming server responses are always JSON
    })
    .catch(error => {
      // Log and re-throw the error for further handling
      console.error('Fetch error:', error);
      throw error;
    });
}

// Scrim API functions
export async function getScrims() {
  return fetchAPI('/scrims', { method: 'GET' });
}

export async function createScrim(name: string) {
  return fetchAPI('/scrim', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function updateScrim(scrimId: string, name: string) {
  return fetchAPI(`/scrim/${scrimId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

export async function deleteScrim(scrimId: string) {
  return fetchAPI(`/scrim/${scrimId}`, { method: 'DELETE' });
}

export async function getScrimTracks(scrimId: string) {
  return fetchAPI(`/scrim/${scrimId}/tracks`, { method: 'GET' });
}

export async function createScrimTrack(scrimId: string, track: {
  youtube_id: string;
  track_index: number;
}) {
  return fetchAPI(`/scrim/${scrimId}/tracks`, {
    method: 'POST',
    body: JSON.stringify(track),
  });
}

export async function createScrimTracks(scrimId: string, tracks: {
  youtube_id: string;
  track_index: number;
}[]) {
  return fetchAPI(`/scrim/${scrimId}/tracks`, {
    method: 'POST',
    body: JSON.stringify({ tracks }),
  });
}

export async function updateScrimTrack(scrimId: string, youtubeId: string, updates: {
  track_index?: number;
}) {
  return fetchAPI(`/scrim/${scrimId}/tracks/${youtubeId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteScrimTrack(scrimId: string, youtubeId: string) {
  return fetchAPI(`/scrim/${scrimId}/tracks/${youtubeId}`, { method: 'DELETE' });
}

export async function updateScrimTrackOffset(scrimId: string, youtubeId: string, offset: number) {
  return fetchAPI(`/scrim/${scrimId}/tracks/${youtubeId}/offset`, {
    method: 'PATCH',
    body: JSON.stringify({ offset }),
  });
}

export async function launchScrim(scrimId: string) {
  return fetchAPI(`/scrim/${scrimId}/launch`, { method: 'POST', body: JSON.stringify({}) });
}

// YouTube API functions
export async function getYoutubeInfo(youtubeId: string) {
  return fetchAPI(`/youtube/${youtubeId}`, { method: 'GET' });
}